export interface BuildStackExecutionResult {
  success: boolean;
  message: string;
  logs: string[];
  executionId: string;
  results?: Array<{
    stepIndex: number;
    beeName: string;
    command: string;
    status: 'success' | 'error' | 'skipped';
    output?: string;
    error?: string;
    duration?: number;
  }>;
}

export interface BuildStackStatusResult {
  executionId: string;
  status: 'running' | 'completed' | 'failed' | 'cancelled';
  progress: number;
  message: string;
}

export async function runBuildStack(stackId: string, steps: Array<{ beeName: string; command: string; inputs?: Record<string, unknown> }>): Promise<BuildStackExecutionResult> {
  try {
    const baseUrl = import.meta.env.VITE_ARCOS_API_URL || 'http://localhost:8000';
    const apiKey = import.meta.env.VITE_ARCOS_API_KEY || 'dev-key';

    const response = await fetch(`${baseUrl}/api/buildstacks/${stackId}/run`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${apiKey}`
      },
      body: JSON.stringify({ stackId, steps })
    });

    if (response.ok) {
      return await response.json();
    } else {
      throw new Error(`HTTP ${response.status}`);
    }
  } catch (error) {
    console.warn('BuildStack API not available, using mock response:', error);
    
    const executionId = `exec-${Date.now()}`;
    const logs = [
      `🚀 Starting BuildStack execution: ${stackId}`,
      '',
      ...steps.map((step, index) => 
        `✅ Step ${index + 1}: ${step.beeName}.${step.command} completed successfully`
      ),
      '',
      `🎉 BuildStack execution completed successfully!`
    ];

    const results = steps.map((step, index) => ({
      stepIndex: index,
      beeName: step.beeName,
      command: step.command,
      status: 'success' as const,
      output: `Mock output for ${step.beeName}.${step.command}`,
      duration: Math.floor(Math.random() * 1000) + 500
    }));

    return {
      success: true,
      message: 'BuildStack executed successfully (mock mode)',
      logs,
      executionId,
      results
    };
  }
}

export async function getBuildStackStatus(executionId: string): Promise<BuildStackStatusResult> {
  try {
    const baseUrl = import.meta.env.VITE_ARCOS_API_URL || 'http://localhost:8000';
    const apiKey = import.meta.env.VITE_ARCOS_API_KEY || 'dev-key';

    const response = await fetch(`${baseUrl}/api/buildstacks/status/${executionId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${apiKey}`
      }
    });

    if (response.ok) {
      return await response.json();
    } else {
      throw new Error(`HTTP ${response.status}`);
    }
  } catch (error) {
    console.warn('BuildStack status API not available, using mock response:', error);
    
    return {
      executionId,
      status: 'completed',
      progress: 100,
      message: 'Mock status response'
    };
  }
}

import type { BuildStack } from '../stores/useBuildStacksStore.js';

export async function getCommunityBuildStacks(): Promise<BuildStack[]> {
  try {
    const baseUrl = import.meta.env.VITE_ARCOS_API_URL || 'http://localhost:8000';
    const apiKey = import.meta.env.VITE_ARCOS_API_KEY || 'dev-key';

    const response = await fetch(`${baseUrl}/api/buildstacks/community`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${apiKey}`
      }
    });

    if (response.ok) {
      return await response.json();
    } else {
      throw new Error(`HTTP ${response.status}`);
    }
  } catch (error) {
    console.warn('Community BuildStacks API not available, using mock response:', error);
    
    return [];
  }
}

export async function getClientBuildStacks(clientId: string): Promise<BuildStack[]> {
  try {
    const baseUrl = import.meta.env.VITE_ARCOS_API_URL || 'http://localhost:8000';
    const apiKey = import.meta.env.VITE_ARCOS_API_KEY || 'dev-key';

    const response = await fetch(`${baseUrl}/api/buildstacks?clientId=${clientId}&category=client`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${apiKey}`
      }
    });

    if (response.ok) {
      return await response.json();
    } else {
      throw new Error(`HTTP ${response.status}`);
    }
  } catch (error) {
    console.warn('Client BuildStacks API not available, using mock response:', error);
    
    return [];
  }
}

export async function publishBuildStack(id: string): Promise<void> {
  try {
    const baseUrl = import.meta.env.VITE_ARCOS_API_URL || 'http://localhost:8000';
    const apiKey = import.meta.env.VITE_ARCOS_API_KEY || 'dev-key';

    const response = await fetch(`${baseUrl}/api/buildstacks/${id}/publish`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${apiKey}`
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
  } catch (error) {
    console.warn('Publish BuildStack API not available, using mock response:', error);
  }
}

export async function unpublishBuildStack(id: string): Promise<void> {
  try {
    const baseUrl = import.meta.env.VITE_ARCOS_API_URL || 'http://localhost:8000';
    const apiKey = import.meta.env.VITE_ARCOS_API_KEY || 'dev-key';

    const response = await fetch(`${baseUrl}/api/buildstacks/${id}/unpublish`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${apiKey}`
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
  } catch (error) {
    console.warn('Unpublish BuildStack API not available, using mock response:', error);
  }
}

export async function runStep(step: { beeName: string; command: string; inputs?: Record<string, unknown> }): Promise<BuildStackExecutionResult> {
  try {
    const baseUrl = import.meta.env.VITE_ARCOS_API_URL || 'http://localhost:8000';
    const apiKey = import.meta.env.VITE_ARCOS_API_KEY || 'dev-key';

    const response = await fetch(`${baseUrl}/api/buildstacks/step/run`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${apiKey}`
      },
      body: JSON.stringify({ step })
    });

    if (response.ok) {
      return await response.json();
    } else {
      throw new Error(`HTTP ${response.status}`);
    }
  } catch (error) {
    console.warn('BuildStack step API not available, using mock response:', error);
    
    const executionId = `step-exec-${Date.now()}`;
    const commandDisplay = step.command === 'healthCheck' ? 'Health Check' : 
                          step.command === 'rollback' ? 'Rollback' : 
                          step.command;
    
    const logs = [
      `🚀 Starting ${step.beeName} ${commandDisplay}...`,
      '',
      step.command === 'healthCheck' ? 
        `✅ Health check passed for ${step.inputs?.platform || 'platform'}` :
      step.command === 'rollback' ?
        `✅ Rollback completed for ${step.inputs?.platform || 'platform'}` :
        `✅ Deploy completed successfully to ${step.inputs?.platform || 'platform'}`,
      '',
      `🎉 ${step.beeName}.${step.command} execution completed!`
    ];

    const results = [{
      stepIndex: 0,
      beeName: step.beeName,
      command: step.command,
      status: 'success' as const,
      output: `Mock output for ${step.beeName}.${step.command} on ${step.inputs?.platform || 'unknown platform'}`,
      duration: Math.floor(Math.random() * 2000) + 1000
    }];

    return {
      success: true,
      message: `${step.beeName}.${step.command} executed successfully (mock mode)`,
      logs,
      executionId,
      results
    };
  }
}
